/**
 * CreatorProBuffetModal — the ONE upgrade popup used everywhere (Jul 2026, Randy).
 *
 * Wraps the full Creator Pro buffet in a dialog. Two entry styles:
 *   • From a locked control (gate)  → preselect=[sku], initialMode="customize"
 *     (opens with that feature + its base pre-checked; owned items show OWNED).
 *   • From the Fan Portal            → preselect=null, initialMode="simple"
 *     (opens in the clean simple pitch; toggling Customize reveals owned state).
 *
 * Ownership + active-subscription come from /me/entitlements + /me/affiliate-status.
 * A non-subscriber sees "activate your Creator subscription first" instead.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useEntitlements, refreshEntitlements } from "@/lib/entitlements";
import { useTrial, setTrial, money, trialDate } from "@/lib/trial";
import CreatorProBuffet from "@/components/CreatorProBuffet";
import EarningsDisclaimer from "@/components/EarningsDisclaimer";
import PartnerProgramNote from "@/components/PartnerProgramNote";

export default function CreatorProBuffetModal({ open, onOpenChange, preselect = null, initialMode = "simple" }) {
  const navigate = useNavigate();
  const { ent } = useEntitlements();
  const [freshEnt, setFreshEnt] = useState(null);
  const [status, setStatus] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Always re-read entitlements fresh so ownership + subscription state are
    // current (a creator who just paid must never see the stale "become a creator" card).
    refreshEntitlements().then(setFreshEnt).catch(() => {});
    api.get("/me/affiliate-status").then((r) => setStatus(r.data)).catch(() => {});
  }, [open]);

  const e = freshEnt || ent;
  const activeSub = !!e?.is_active_subscriber;

  // Oct 2026 (TRIAL_PLAN.md §8.6) — a trialing creator adds tools to the
  // day-14 bill instead of paying today. "Buy now" stays for anyone who would
  // rather own it immediately.
  const { trial } = useTrial();
  const [trialBusy, setTrialBusy] = useState(null);

  // During a trial `owned` is all-True (every tool is unlocked), which would
  // paint the whole buffet as OWNED and hide both the prices and the
  // include-at-day-14 controls. The real ledger is what ownership means here.
  const ownedSource = (trial?.live ? e?.owned_real : e?.owned) || {};
  const ownedKeys = Object.keys(ownedSource).filter((k) => ownedSource[k]);

  const commitSku = async (sku, cents) => {
    setTrialBusy(sku);
    try {
      const r = await api.post("/trial/commit", { sku, price_cents: cents, screen: "buffet" });
      setTrial(r.data?.trial);
      toast.success(`Added to your day-14 bill — ${money(cents)} on ${trialDate(trial?.ends_at)}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add that to your list.");
    } finally { setTrialBusy(null); }
  };

  const revokeSku = async (sku) => {
    setTrialBusy(sku);
    try {
      const r = await api.delete(`/trial/commit/${encodeURIComponent(sku)}`);
      setTrial(r.data?.trial);
      toast.success("Taken off your day-14 bill.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove that.");
    } finally { setTrialBusy(null); }
  };

  const commitBundle = async (cents) => {
    setTrialBusy("bundle");
    try {
      const r = await api.post("/trial/commit-bundle", { price_cents: cents, screen: "buffet" });
      setTrial(r.data?.trial);
      toast.success(`Everything is on your day-14 bill — ${money(cents)}, one charge.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add the bundle.");
    } finally { setTrialBusy(null); }
  };

  const startCheckout = async ({ full_bundle, selected_skus }) => {
    setRedirecting(true);
    try {
      const r = await api.post("/affiliate/start-checkout", {
        origin_url: window.location.origin,
        selected_skus,
        full_bundle,
      });
      // Feb 24, 2026 (Randy pt-founder-bypass) — no URL means the
      // founder short-circuit fired. Close the modal and toast the
      // grant so the surrounding UI can refresh entitlement state.
      if (r.data?.already_granted) {
        toast.success(r.data.message || "Founder access — Creator Pro auto-granted.");
        setRedirecting(false);
        onOpenChange(false);
        try { window.dispatchEvent(new CustomEvent("profile-updated")); } catch { /* fine */ }
        return;
      }
      if (r.data?.url) window.location.href = r.data.url;
      else throw new Error("no url");
    } catch (err) {
      setRedirecting(false);
      toast.error(err.response?.data?.detail || "Could not start checkout.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-gradient-to-br from-[#1C1B1A] to-[#3A4A3E] text-[#FDFCF8] border-0 overflow-y-auto top-[calc(env(safe-area-inset-top,0px)+1rem)] translate-y-0 max-h-[calc(100dvh-env(safe-area-inset-top,0px)-2rem)] sm:top-1/2 sm:-translate-y-1/2 sm:max-h-[88vh]"
        data-testid="creator-pro-buffet-modal"
      >
        <DialogTitle className="sr-only">Unlock the full My Silo toolkit</DialogTitle>
        <DialogDescription className="sr-only">Creator Pro upgrade — choose the tools to unlock.</DialogDescription>
        {!activeSub ? (
          <div data-testid="gate-state-nosub">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C16E5A] flex items-center gap-2">
              <Crown size={12} weight="duotone" /> Creator Pro
            </div>
            <h3 className="font-serif-display text-2xl leading-tight mt-1">Unlock the full My Silo toolkit</h3>
            <p className="text-sm text-[#E5E5E0]/90 mt-3">
              Activate your Creator subscription first — Creator Pro is a one-time upgrade on top of it.
            </p>
            <button
              onClick={() => { onOpenChange(false); navigate("/dashboard/account-settings"); }}
              data-testid="gate-go-billing"
              className="mt-4 bg-[#C16E5A] text-white hover:bg-[#A85E4A] px-5 py-2.5 rounded-md text-sm font-medium transition-colors"
            >
              Go to Account &amp; Billing
            </button>
          </div>
        ) : (
          <>
            <CreatorProBuffet
              status={status}
              owned={ownedKeys}
              preselect={preselect}
              initialCustomize={initialMode === "customize"}
              onCheckout={startCheckout}
              redirecting={redirecting}
              trial={trial}
              trialBusy={trialBusy}
              onTrialCommit={commitSku}
              onTrialRevoke={revokeSku}
              onTrialBundle={commitBundle}
            />
            <div className="mt-5 pt-5 border-t border-white/10" data-testid="buffet-partner-note">
              <p className="text-[11px] text-[#E5E5E0]/80 mb-1.5">
                <PartnerProgramNote testId="buffet-partner-program-note" />
              </p>
              <EarningsDisclaimer variant="short" className="text-[#A8A8A0]" />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
